import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchInvoices } from "../../store/thunks/invoiceThunk";
import {
  setCurrentInvoice,
  setSelectedInvoice,
} from "../../store/slices/invoiceSlice";
import SearchDropdown from "../../applicationUi/components/SearchDropdown";
import InvoiceGenerator from "../../applicationUi/components/InvoiceGenerator";
import InvoicePaymentModal from "../../modals/InvoicePaymentModal";
import Table from "../../applicationUi/components/Table";
import QuickBooksLogo from "../../assets/quickbooks-1.svg";

const InvoicesPage = () => {
  const dispatch = useDispatch();
  const invoices = useSelector((state) => state.invoices?.invoices || []);
  const isLoading = useSelector(
    (state) => state.invoices?.status === "loading"
  );
  const currentPage = useSelector((state) => state.invoices?.currentPage || 1);
  const totalPages = useSelector((state) => state.invoices?.totalPages || 1);
  const totalInvoices = useSelector(
    (state) => state.invoices?.totalInvoices || 0
  );
  const error = useSelector((state) => state.invoices?.error || null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showInvoiceGenerator, setShowInvoiceGenerator] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });
  const [dateFilter, setDateFilter] = useState("7"); // Default to 7 days

  useEffect(() => {
    dispatch(
      fetchInvoices({
        currentPage,
        sortKey: sortConfig.key,
        sortDirection: sortConfig.direction,
        dateFilter,
      })
    );
  }, [dispatch, currentPage, sortConfig, dateFilter]);

  const handleRowClick = (clickedInvoice) => {
    const invoice = invoices.find((inv) => inv._id === clickedInvoice);

    dispatch(setCurrentInvoice(invoice));
    setSelectedInvoice(invoice);
    console.log("[INVOICESPAGE] - Clicked on this invoice", invoice);
    setShowInvoiceGenerator(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedInvoice(null);
  };

  const handleInvoiceGeneratorClose = () => {
    setShowInvoiceGenerator(false);
    setSelectedInvoice(null);
  };

  const handlePageChange = (newPage) => {
    dispatch(
      fetchInvoices({
        currentPage: newPage,
        sortKey: sortConfig.key,
        sortDirection: sortConfig.direction,
        dateFilter,
      })
    );
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleDateFilterChange = (event) => {
    setDateFilter(event.target.value);
  };

  if (error) {
    const errorMessage =
      typeof error === "object" && error.message
        ? error.message
        : "An error occurred";
    return <div>Error: {errorMessage}</div>;
  }

  return (
    <div>
      {isLoading ? (
        <p>Loading invoices...</p>
      ) : (
        <div>
          <div className="py-4 flex justify-between items-center">
            <div className="flex-1 mr-4">
              <SearchDropdown
                prefix=":invoice:"
                displayFields={[
                  "invoiceId",
                  "client.fullName",
                  "amount",
                  "status",
                ]}
                onSelection={(selectedInvoice) => {
                  // Handle invoice selection
                }}
                placeholder={
                  "Search for an invoice by ID, client name, or status"
                }
              />
            </div>
            <div className="w-48">
              <select
                value={dateFilter}
                onChange={handleDateFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-sm shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>
                <option value="all">All Time</option>
              </select>
            </div>
          </div>

          <Table
            columns={[
              {
                header: "Invoice ID",
                accessor: "invoiceId",
                sortable: true,
              },
              {
                header: "Service Date",
                accessor: (row) => {
                  if (!row.serviceDate) return "N/A";
                  const date = new Date(row.serviceDate);
                  return date instanceof Date && !isNaN(date)
                    ? date.toLocaleDateString("en-US", {
                        weekday: "short",
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })
                    : "Invalid Date";
                },
                sortable: true,
              },
              {
                header: "Invoice Date",
                accessor: (row) => {
                  if (!row.createdAt) return "N/A";
                  const date = new Date(row.createdAt);
                  return date instanceof Date && !isNaN(date)
                    ? date.toLocaleDateString("en-US", {
                        weekday: "short",
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })
                    : "Invalid Date";
                },
                sortable: true,
              },
              {
                header: "Client",
                accessor: (row) =>
                  row.client
                    ? `${row.client.firstName || "Unknown"} ${
                        row.client.lastName || "Unknown"
                      }`
                    : "Unknown",
                sortable: true,
              },
              {
                header: "Clinician",
                accessor: (row) =>
                  row.clinician
                    ? `${row.clinician.firstName || "Unknown"} ${
                        row.clinician.lastName || "Unknown"
                      }`
                    : "Unknown",
                sortable: true,
              },
              {
                header: "Amount",
                accessor: "amount",
                sortable: true,
                cell: (row) => `$${row.amount?.toFixed(2) || "0.00"}`,
              },
              {
                header: "Split Invoice",
                accessor: (row) => {
                  if (!row.splitBilling?.isSplit) {
                    return null;
                  }
                  
                  return (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-200">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                      Split
                    </span>
                  );
                },
                sortable: true,
              },
              {
                header: "Status",
                accessor: "status",
              },
              {
                header: "QuickBooks",
                accessor: (row) => (
                  <div className="flex items-center">
                    {row.QBOInvoiceId && (
                      <img
                        src={QuickBooksLogo}
                        alt="QuickBooks"
                        className="ml-2 h-5 w-5"
                        title="Loaded to QuickBooks"
                      />
                    )}
                  </div>
                ),
              },
            ]}
            data={invoices}
            onRowClick={handleRowClick}
            title="Invoices"
            description={`List of all invoices (${totalInvoices} total)`}
            showPagination={true}
            currentPage={currentPage}
            totalPages={totalPages}
            totalResults={totalInvoices}
            onPageChange={handlePageChange}
            sortConfig={sortConfig}
            onSortChange={handleSort}
          />

          {showModal && selectedInvoice && (
            <InvoicePaymentModal
              invoice={selectedInvoice}
              onClose={handleModalClose}
            />
          )}

          {showInvoiceGenerator && selectedInvoice && (
            <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
              <div className="relative bg-white rounded-sm shadow-xl max-w-7xl w-full m-4">
                <div className="p-4 sm:p-6 lg:p-8">
                  <button
                    onClick={handleInvoiceGeneratorClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:outline-none"
                    aria-label="Close">
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                  <InvoiceGenerator
                    data={selectedInvoice}
                    initialViewMode={true}
                    onClose={handleInvoiceGeneratorClose}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;
